import { ModelProps } from '@adobe/aem-react-editable-components'
import { StoreDetailsProps as UIFoundationStoreDetailsProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/store/store-details/types'
import type {
  Store,
  Address,
  OpeningHour,
  StoresErrorResponse
} from '../../../services/stores/stores-sdk'

export enum OpenStatus {
  Open = 'OPEN',
  Closed = 'CLOSED',
  NotAvailable = 'NOT_AVAILABLE'
}

export enum StoreDetailsTypeKeys {
  StoreDetailsConfig = 'storeDetailsConfig',
  StoreLocatorConfig = 'storeLocatorConfig',
  BreadCrumbConfig = 'storeBreadCrumbConfig'
}

export interface TransformStoreDetailsInput {
  storeDetailsConfig: StoreDetailsModelProps[StoreDetailsTypeKeys.StoreDetailsConfig]
  storeLocatorConfig: StoreDetailsModelProps[StoreDetailsTypeKeys.StoreLocatorConfig]
  storeData?: Store
}

interface IObjectKeys {
  [key: string]: string
}

export interface DaysProps extends IObjectKeys {
  monday: string
  tuesday: string
  wednesday: string
  thursday: string
  friday: string
  saturday: string
  sunday: string
}

export interface CategoryProps {
  id: string
  image: string
  hoveredImage?: string
  ariaLabel: string
}

export interface StoreServiceConfig {
  types: string
  description: string
  label: string
  icon: string
}

export interface PaymentMethodConfig {
  key: string
  alt: string
  src: string
}

export interface StoreDetailsModelProps {
  storeLocatorConfig: {
    googleMapsAPIKey: string
    showBookAnAppointmentServiceEntry: boolean
    bookAnAppointmentLabel?: string
    bookAnAppointmentLink?: string
    loginPageLink?: string
    openedUntilLabel?: string
    willOpenAtLabel?: string
    enable12HourFormat?: boolean
    enableMilesAsDistanceUnit?: boolean
    enableExpandedOpeningHoursView?: boolean
    categories: Array<CategoryProps>
    days: DaysProps
    openingHoursTitle: string
    closedLabel: string
    openingHoursNotAvailable?: string
    showDirectionsLabel: string
    distanceLabel: string
    servicesListTitle: string
    services: Array<StoreServiceConfig>
    storeDirectoryPageUrl?: string
    storeDirectoryCities?: string[]
    storeMapPageUrl?: string
    storeAddressFormatTemplate?: string
    mapUserIcon?: string
  }
  storeId: string
  storeImage: {
    imagePresetsArray: string
    image: {
      id: string
      src: string
      sizes: string
      lazyEnabled: boolean
      srcset: string
      ':type': string
    }
    height: string
    width: string
    srcset: string
    assetFileReference: string
    imageRenditions: boolean
    renditionWidths: Array<string>
    renditionHeights: Array<string>
    dmImage: boolean
    ':type': string
  }
  initialStoreData?: Store | StoresErrorResponse
  storeDetailsConfig: {
    paymentMethodsTitle: string
    payments: Array<PaymentMethodConfig>
    mapZoomLevel: number
    mapButtonLabel?: string
    breadcrumbCountryLabel?: string
    goToMapViewLabel?: string
  }
  xfContentPath?: string
  xfContent?: ModelProps
}

export interface TransformedStoreDetailsData {
  labels: {
    openedUntilLabel?: string
    willOpenAtLabel?: string
    closedLabel: string
    openingHoursNotAvailable?: string
    openingHoursTitle: string
    bookAnAppointmentLabel?: string
    paymentMethodsTitle: string
    mapButtonLabel?: string
    showDirectionsLabel: string
    distanceLabel: string
    servicesListTitle?: string
    breadcrumbCountryLabel?: string
    goToMapViewLabel?: string
  }
  configs: {
    categories: Array<CategoryProps>
    mapZoomLevel: number
    mapUserIcon?: string
    paymentsConfig: Array<PaymentMethodConfig>
    // store locator configs
    days: DaysProps
    loginPageLink?: string
    googleMapsAPIKey?: string
    bookAnAppointmentLink?: string
    showBookAnAppointmentServiceEntry: boolean
    enable12HourFormat?: boolean
    enableMilesAsDistanceUnit?: boolean
    enableExpandedOpeningHoursView?: boolean
    servicesConfig: Array<StoreServiceConfig>
    storeDirectoryPageUrl?: string
    storeDirectoryCities?: string[]
    storeMapPageUrl?: string
    // store data
    storeAddress?: Store['address']
    storeLocation?: Store['address']['location']

    shouldShowBookAnAppointment: boolean
    shouldShowMapService: boolean
    storeAddressFormatTemplate?: string
  }
}

export interface StoreLDJsonParams {
  storeName: string
  storeUrl: string
  telephone: string
  storeImage?: string
  description: string
  address?: Address
  openingHours?: OpeningHour[]
}

export interface OpeningHoursSpecification {
  '@type': 'OpeningHoursSpecification'
  dayOfWeek: string | string[]
  opens?: string
  closes?: string
}

export interface StoreLDJson {
  '@context': string
  '@type': string
  '@id': string
  name: string
  description: string
  url: string
  telephone?: string
  image?: string[]
  address?: {
    '@type': string
    streetAddress?: string
    addressLocality?: string
    postalCode?: string
    addressCountry?: string
  }
  geo?: {
    '@type': string
    latitude: number
    longitude: number
  }
  openingHoursSpecification?: OpeningHoursSpecification[]
}

export type StoreBreadCrumbConfig =
  UIFoundationStoreDetailsProps[StoreDetailsTypeKeys.BreadCrumbConfig]

export type StoreDetailsComponentProps = UIFoundationStoreDetailsProps

export interface DataLayerItem {
  key: string
  value: string
}
